import { AxiosError } from 'axios'
import classNames from 'classnames'
import { t } from 'i18next'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Fieldset } from 'primereact/fieldset'
import { Toast } from 'primereact/toast'
import { ToggleButton } from 'primereact/togglebutton'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  API_ACCOUNT,
  API_ACCOUNTS,
  API_APPRENTICE,
  API_PERSON,
} from '../../../../constants'
import { Account } from '../../../../model/account.model'
import { DropdownItem } from '../../../../model/dropdownItem.model'
import {
  EntityStatus,
  RequestMethod,
  UserAccountType,
} from '../../../../model/enums/enums'
import {
  Apprentice,
  ApprenticeBody,
  Person,
} from '../../../../model/person.model'
import { State } from '../../../../model/state.model'
import {
  addAccount,
  removeAccounts,
  setAccounts,
  updateAccount,
} from '../../../../redux/basic/account/accountActions'
import {
  apprenticeAdded,
  apprenticeUpdated,
} from '../../../../redux/basic/apprentice/apprenticeReducer'
import { employeeAdded } from '../../../../redux/basic/employee/employeeReducer'
import { personRemoved } from '../../../../redux/basic/person/personReducer'
import { updatePersonInStore } from '../../../../redux/basic/updatePerson'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import {
  addAccountToDb,
  updatePersonInDb,
} from '../../../../services/api/mokaiApiService'
import { BackButton } from '../../../util/button/BackButton'
import { showErrorToast } from '../../../util/checkmark-toast/CheckmarkToast'
import { actionBody } from '../../../util/data-table/cell-body/ActionBody'
import { dateBody } from '../../../util/data-table/cell-body/BirthdayBody'
import { FilterElement } from '../../../util/data-table/Filter/GlobalFilter'
import { DeleteConfirmationDialog } from '../../../util/dialog/DeleteConfirmationDialog'

export const AccountList = (): JSX.Element => {
  const [filterString, setFilterString] = useState<string>('')
  const [globalFilter, setGlobalFilter] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const setFilter = (e: any) => {
    const val = e
    let _filter = { ...globalFilter }
    _filter['global'].value = val
    setGlobalFilter(_filter)
    setFilterString(val)
  }

  const resetFilter = () => {
    setGlobalFilter({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })
    setFilterString('')
  }

  const accounts = useAppSelector((state) => state.accounts)

  const accountlessPeople = useAppSelector((state) => state.persons)
  const [selectedPersons, setSelectedPersons] = useState<Person[]>([])

  const [loading, setLoading] = useState<boolean>(false)

  // we need to deactivate the is active button until we receive a response,
  // else we can get a timing issue with the identity platform
  const [updating, setUpdating] = useState<boolean>(false)

  const [isUserDialogVisible, setUserDialogVisible] = useState<boolean>(false)

  const [isDeleteDialogActive, setDeleteDialogActive] = useState<boolean>(false)

  const toast = useRef<Toast>(null)

  const userRoles: DropdownItem[] = [
    { label: t<string>('core.role.apprentice'), value: 1 },
    { label: t<string>('core.role.ausbilder'), value: 2 },
    { label: t<string>('core.role.berufsbildner'), value: 3 },
    { label: t<string>('core.role.admin'), value: 4 },
  ]

  const [selectedUserRole, setSelectedUserRole] = useState<number>(
    userRoles[0].value
  )

  const dispatcher = useAppDispatch()

  useEffect(() => {
    const loadAccounts = async () => {
      const loadedAccs: State<any> = await handleRequest(
        API_ACCOUNTS(),
        RequestMethod.GET
      )
      setLoading(false)
      if (!loadedAccs?.data) {
        console.log('[Account Management] No accounts found.')
        return
      }
      dispatcher(setAccounts(loadedAccs.data))
    }
    if (loading) {
      return
    }
    setLoading(true)
    dispatcher(removeAccounts())
    loadAccounts()
  }, [])

  const apprentices = useAppSelector((state) => state.apprentices)

  const deactivatePerson = async (account: Account) => {
    console.log(
      '[Account] Give the person the same status as the account, e.g. inactive = inactive person'
    )

    const p = await handleRequest<Person>(
      API_PERSON(account.id_person),
      RequestMethod.PUT,
      { person_id: account.id_person, is_active: account.is_active }
    )

    if (p.data && !p.error) {
      console.log('[Account] successfully changed the person activity status')

      if (account.user_account.id_user_account === UserAccountType.Apprentice) {
        console.log(
          '[Account] The person is an apprentice,therefore we do change the apprentice state accordingly.'
        )
        const apprentice: Apprentice | undefined = apprentices.find(
          (x) => x.person_id === p.data?.person_id
        )
        if (apprentice?.apprentice?.apprentice_id) {
          const apprenticeBody: ApprenticeBody = {
            apprentice_id: apprentice.apprentice.apprentice_id,
            is_active: account.is_active,
          }

          const a = await handleRequest<ApprenticeBody>(
            API_APPRENTICE(apprentice.apprentice.apprentice_id),
            RequestMethod.PUT,
            apprenticeBody
          )
          if (a.data && !a.error) {
            console.log(
              '[Account] The apprentice status is set accordingly to the account status.'
            )
            const apprenticeCopy: Apprentice = {
              ...apprentice,
              is_active: account.is_active,
              apprentice: { ...a.data },
            }
            dispatcher(apprenticeUpdated(apprenticeCopy))
          }
        }
      } else {
        p.data.user_account_type = account.user_account.id_user_account
        updatePersonInStore(p.data, dispatcher)
      }
    } else {
      console.error(
        '[Account] Error occured updating the person activity status',
        p.error
      )
    }
  }

  const handleActivity = async (account: Account) => {
    setUpdating(true)
    const currentStatus = account.is_active
    const activeStatus =
      currentStatus === EntityStatus.Active
        ? EntityStatus.Inactive
        : EntityStatus.Active
    const body: {
      account_id: number
      id_person: number
      is_active: EntityStatus
    } = {
      account_id: account.account_id,
      id_person: account.id_person,
      is_active: activeStatus,
    }
    const result = await handleRequest<Account>(
      API_ACCOUNT(body.account_id),
      RequestMethod.PUT,
      body
    )
    setUpdating(false)
    if (result.data && !result.error) {
      console.info(`[Account Management] Successfully changed active state of account of 
			 ${account.account_name}.`)
      dispatcher(updateAccount(result.data))

      // set person inactive if account is deactivated
      await deactivatePerson(result.data)
    } else {
      if (result?.httpStatus === 409) {
        console.warn('[Account Management] Email duplicated', account.email)
        showErrorToast(
          toast,
          t<string>('core.error.duplicated_email.title'),
          t<string>('core.error.duplicated_email.message')
        )
        return
      }
      toast?.current?.show({
        className: 'mt-7',
        closable: true,
        summary: t<string>('core.error.account.edit.title'),
        detail: t<string>('core.error.account.edit.message'),
        severity: 'error',
        life: 12000,
      })
      console.warn(
        `[Account Management] Failed to change active state of the account of ${account.account_name}.`
      )
    }
  }

  const activeBody = (account: Account) => {
    if (account.is_active === undefined || account.is_active === null) {
      return ''
    }

    return (
      <ToggleButton
        checked={account.is_active === EntityStatus.Active}
        type={'button'}
        offIcon={'fa-solid fa-xmark'}
        onIcon={'fa-solid fa-check'}
        onLabel={t<string>('core.active')}
        offLabel={t<string>('core.inactive')}
        disabled={updating}
        className={classNames(
          { 'icon-red': account.is_active === EntityStatus.Inactive },
          ' p-button-raised min-width-120'
        )}
        onChange={() => handleActivity(account)}
      />
    )
  }

  const userAccountBody = (rowData: Account): JSX.Element => {
    const userAccountId = rowData?.user_account?.id_user_account
    const displayText = t<string>(
      `core.role.${rowData?.user_account?.user_account_key}`
    )
    let color = ''
    switch (userAccountId) {
      case 1:
        color = 'dark-blue'
        break
      case 2:
        color = 'dark-green'
        break
      case 3:
        color = 'ocker'
        break
      case 4:
        color = 'light-red'
        break
    }
    return (
      <span className={'min-width-120 mr-2 tag-' + color}>{displayText}</span>
    )
  }

  const onUserRoleChange = (e: { value: number }) => {
    setSelectedUserRole(e.value)
  }

  const closeUserRoleDialog = () => {
    setUserDialogVisible(false)
  }
  const openUserRoleDialog = () => {
    setUserDialogVisible(true)
  }
  const addToDb = async (): Promise<boolean> => {
    const promises = []
    for (let i = 0; i < selectedPersons.length; i++) {
      const body = {
        id_person: selectedPersons[i].person_id,
        user_account: {
          id_user_account: selectedUserRole,
        },
        account_name: selectedPersons[i].full_name,
        email: selectedPersons[i].email,
      }
      promises.push(addAccountToDb(body))
    }
    return Promise.all(promises)
      .then((results: State<Account>[]) => {
        let errorMsg = ''
        for (let i = 0; i < results.length; i++) {
          if (results[i]?.error) {
            if (results[i]?.httpStatus === 409) {
              console.warn('[Account Management] Email duplicated')
              showErrorToast(
                toast,
                t<string>('core.error.duplicated_email.title'),
                t<string>('core.error.duplicated_email.message')
              )
            } else {
              errorMsg += results[i]?.error?.message
            }
            return false
          }
          const returnedAccount: Account | undefined = results[i].data
          if (!returnedAccount) {
            console.warn('[Account Management] account result is empty.')
            return false
          }

          const accountType = returnedAccount?.user_account?.id_user_account
          const newSelectedPersons = { ...selectedPersons }

          if (accountType) {
            newSelectedPersons[i] = {
              ...newSelectedPersons[i],
              user_account_type: accountType,
            }
            setSelectedPersons(newSelectedPersons)
          }

          // 1 = apprentice
          if (selectedUserRole === 1) {
            let apprent = newSelectedPersons[i] as Apprentice
            dispatcher(apprenticeAdded(apprent))
          } else {
            dispatcher(employeeAdded(newSelectedPersons[i]))
          }
          const id = newSelectedPersons[i]?.person_id
          if (id !== undefined) {
            // remove the person from the person list since it now has an account
            dispatcher(personRemoved(id))
            dispatcher(addAccount(returnedAccount))
          }
        }

        if (errorMsg !== '') {
          showErrorToast(
            toast,
            t<string>('core.action.error.add.title'),
            errorMsg
          )
        }
        return true
      })
      .catch((errors: any[]) => {
        let duplicatedEmailsMsg = ''
        let errorMsg = ''
        for (let i = 0; i < errors.length; i++) {
          if (errors[i].status === 409) {
            duplicatedEmailsMsg += ` ${errors[i].message}\n`
          } else {
            errorMsg += errors[i].message
          }
        }
        if (duplicatedEmailsMsg !== '') {
          showErrorToast(
            toast,
            t<string>('core.action.error.duplicated_email.title'),
            t<string>('core.action.error.duplicated_email.message')
          )
        }

        if (errorMsg !== '') {
          showErrorToast(
            toast,
            t<string>('core.action.error.add.title'),
            errorMsg
          )
        }

        console.error(errors)
        return false
      })
  }

  const assignUserRoles = async () => {
    if (selectedPersons?.length === 0 || !selectedUserRole) {
      console.log(
        'pressed on assign user role but either person or/and role are not selected.'
      )
      return
    }

    const wasSuccessful = await addToDb()

    if (wasSuccessful) {
      toast?.current?.show({
        className: '',
        closable: true,
        content: undefined,
        contentClassName: '',
        contentStyle: undefined,
        sticky: false,
        style: undefined,
        severity: 'success',
        summary: t<string>('core.action.confirmed.title'),
        detail: t<string>('core.action.confirmed.message'),
        life: 5000,
      })

      console.log(
        '[Account Management] User role successfully assigned and account(s) created.'
      )

      setSelectedPersons([])
    } else {
      console.warn(
        '[Account Management] It was not possible to create all accounts.'
      )
    }
    setSelectedUserRole(userRoles[0].value)
    closeUserRoleDialog()
  }

  // Open the delete dialog if reports are selected
  const openDeleteDialog = (row: any) => {
    if (row === undefined) {
      return
    }
    setDeleteDialogActive(true)
  }

  // close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogActive(false)
  }

  const getDeleteMessage = () => {
    if (!selectedPersons?.length) {
      return
    }
    if (selectedPersons.length === 1) {
      return t<string>('dialog.delete.person.question_single_delete')
    }
    return t<string>('dialog.delete.question_multi_delete', {
      length: selectedPersons?.length,
    })
  }

  // delete the selected entries by accepting the delete dialog popup
  const deleteSelectedEntries = async () => {
    if (selectedPersons?.length <= 0) {
      closeDeleteDialog()
      return
    }

    const body: Person = {
      is_active: EntityStatus.Inactive,
    }

    for (let i = 0; i < selectedPersons.length; i++) {
      const id = selectedPersons[i]?.person_id
      if (id !== undefined) {
        await updatePersonInDb(id, body)
          .then(() => {
            if (id) {
              dispatcher(personRemoved(id))
            }
          })
          .catch((error: AxiosError) => {
            // todo
            console.error(error)
          })
      }
    }
    closeDeleteDialog()
    setSelectedPersons([])
  }

  const selectedPersonList = (
    <>
      <h3 className={'ml-2 mb-2'}>{t<string>('account.selected_persons')}</h3>
      {selectedPersons?.map((person: Person, key: number) => {
        return (
          <span key={key}>
            <p className={'ml-2 mb-2'}>
              <i className={'fas fa-user icon-orange'} /> {person.first_name}{' '}
              {person.last_name}
            </p>
          </span>
        )
      })}
    </>
  )

  const emailStringBody = (rowData: any) => {
    return (
      <Button
        className={'p-button-link button-in-text'}
        type={'button'}
        key={'email'}
        id={'email'}
        onClick={() => window.open(`mailto:${rowData.email}`)}
      >
        {rowData.email}
      </Button>
    )
  }

  const getLabel = () =>
    selectedPersons?.length <= 1
      ? t<string>('account.assign_role')
      : t<string>('account.assign_roles')

  const navigate = useNavigate()

  const edit = (account: Account) => {
    console.log('[Account Management] Routing to account edit view.')
    navigate(`${AuthRoutes.accounts}/${account.account_id}`)
  }

  const rowClass = (account: Account) => {
    return {
      'inactive-row':
        account?.is_active !== undefined &&
        account.is_active === EntityStatus.Inactive,
    }
  }

  return (
    <Fragment>
      <BackButton
        translationKey={'navigation.go_human_resources'}
        route={AuthRoutes.hrm}
      />

      <Card className={'card mt-2 mb-2 mr-2 p-2 mokai-gradient'}>
        <div
          className={'flex justify-content-between flex-wrap card-container '}
        >
          <span className={'flex align-items-center justify-content-center'}>
            <b className={'mr-1'}>
              {accounts.length + accountlessPeople.length}
            </b>
            {t<string>('core.statistic.count_people_in_system')}
          </span>
          <span className={'flex align-items-center justify-content-center'}>
            <b className={'mr-1'}>
              {accounts.filter((a) => a.is_active === 1).length}
            </b>
            {t<string>('account.active')}
          </span>
          <span className={'flex align-items-center justify-content-center'}>
            <b className={'mr-1'}>
              {accounts.filter((a) => a.is_active === 0).length}
            </b>
            {t<string>('account.inactive')}
          </span>
        </div>
      </Card>

      <FilterElement
        id={'filter_accounts'}
        globalFilter={filterString}
        setGlobalFilter={setFilter}
        resetFilter={resetFilter}
      />
      <Fieldset
        legend={t<string>('account.accounts')}
        style={{ maxHeight: '40vh' }}
        className={'mt-2'}
        toggleable={true}
      >
        <DataTable
          value={accounts}
          scrollHeight="30vh"
          scrollable
          responsiveLayout="scroll"
          resizableColumns
          columnResizeMode={'fit'}
          dataKey="account_id"
          emptyMessage={t<string>('core.empty_message')}
          loadingIcon={'pi pi-spin pi-spinner'}
          rowHover
          removableSort
          rowClassName={rowClass}
          stripedRows
          filters={globalFilter}
          globalFilterFields={['account_id', 'account_name', 'email']}
        >
          <Column
            field="account_id"
            header={t<string>('account.account_id')}
            sortable
            style={{ maxWidth: '80px' }}
            dataType={'numeric'}
          />
          <Column
            field="account_name"
            header={t<string>('account.account_name')}
            sortable
            dataType={'text'}
          />
          <Column
            field="email"
            header={t<string>('account.email')}
            sortable
            body={emailStringBody}
            dataType={'text'}
          />
          <Column
            field="user_account.user_account_key"
            header={t<string>('account.role')}
            sortable
            body={userAccountBody}
            dataType={'numeric'}
          />
          <Column
            field="created_on"
            header={t<string>('core.created_on')}
            sortable
            bodyClassName={'secondary-text small-text'}
            body={(e: any) => dateBody(e.created_on)}
            dataType={'date'}
          />

          <Column
            field="is_active"
            header={t<string>('account.status')}
            sortable
            body={activeBody}
            dataType={'numeric'}
            style={{ maxWidth: '160px' }}
          />
          <Column
            field={'account_id'}
            bodyClassName={'justify-content-end'}
            style={{ maxWidth: '60px' }}
            body={actionBody(edit)}
          />
        </DataTable>
      </Fieldset>

      <Fieldset
        className={'mt-3'}
        legend={t<string>('account.no_account')}
        toggleable={true}
      >
        <div className="p-fluid formgrid grid">
          <div className={'col-12 md:col-9 mb-2'}>
            <DataTable
              value={accountlessPeople}
              scrollable
              dataKey="person_id"
              emptyMessage={t<string>('core.empty_message')}
              resizableColumns
              columnResizeMode="fit"
              scrollHeight={'40vh'}
              rowHover={true}
              stripedRows
              selection={selectedPersons}
              onSelectionChange={(e) => setSelectedPersons(e.value)}
              filters={globalFilter}
              globalFilterFields={['full_name', 'email']}
            >
              <Column selectionMode="multiple" style={{ maxWidth: '40px' }} />
              <Column
                field="full_name"
                header={t<string>('person.full_name')}
                sortable
              />
              <Column
                field="email"
                header={t<string>('person.email')}
                sortable
              />
              <Column
                field="last_change"
                header={t<string>('person.last_change')}
                sortable
                bodyClassName={'secondary-text small-text'}
                body={(e: Person) => dateBody(e.last_change)}
                style={{ maxWidth: '180px' }}
              />
            </DataTable>
          </div>
          <div className={'col-12 md:col-3'}>
            <Button
              label={getLabel()}
              onClick={openUserRoleDialog}
              className={'p-button-outlined'}
              disabled={!selectedPersons?.length}
            />
            <Button
              label={t<string>('core.delete_selected')}
              onClick={openDeleteDialog}
              className="p-button-danger mt-2 p-button-outlined"
              disabled={!selectedPersons?.length}
            />
          </div>

          <Dialog
            appendTo={document.body}
            visible={isUserDialogVisible}
            onHide={closeUserRoleDialog}
            header={t<string>('account.assign_role_to_user')}
            resizable={true}
            breakpoints={{ '960px': '75vw', '640px': '100vw' }}
            style={{ width: '50vw' }}
          >
            {selectedPersons &&
              selectedPersons?.length > 0 &&
              selectedPersonList}

            <div className={'col-12 grid p-fluid '}>
              <div className={'field md:col-6'}>
                <Dropdown
                  value={selectedUserRole}
                  options={userRoles}
                  onChange={onUserRoleChange}
                  optionLabel="label"
                  optionValue={'value'}
                  appendTo={document.body}
                  placeholder={t<string>('account.select_role')}
                />
              </div>
              <div className={'field md:col-6'}>
                <Button
                  label={getLabel()}
                  onClick={assignUserRoles}
                  disabled={!selectedPersons?.length || !selectedUserRole}
                />
              </div>
            </div>
          </Dialog>

          <DeleteConfirmationDialog
            onClose={() => closeDeleteDialog()}
            onDelete={() => deleteSelectedEntries()}
            visible={isDeleteDialogActive}
            dataToDelete={selectedPersons}
            title={t<string>('core.persons')}
            message={getDeleteMessage()}
          />
        </div>
      </Fieldset>
      <Toast ref={toast} position={'bottom-right'} />
    </Fragment>
  )
}